import { FC, Fragment, useState } from 'react';
import {
	Alert,
	Button,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Table,
	Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { FormAlert, useForm } from '@/libs/antd/form';
import {
	addFRMOfficeObservationCollateral,
	deleteFRMOfficeObservationCollateral,
	updateFRMOfficeObservationCollateral,
} from '@/libs/fleet-api/services/visit-reports';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import rule from '@/libs/antd/rules';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useFRMVisitReportPageContext } from './VisitReportOfficePage';
import { OfficeObservationCollateral } from '@/libs/fleet-api/services/types';
import dayjs from 'dayjs';
import { getProspectInformation } from '@/libs/fleet-api/services/prospect';
import { useModals } from '@/components/modals/ModalProvider';

type VisitReportCollateralForm = {
	input?: OfficeObservationCollateral<'payload'>;
	lists: OfficeObservationCollateral<'payload'>[];
};

const FRMVisitReportOfficeCollateralUnit: FC = () => {
	const modalAPI = useModals();
	const { surveyInformation, officeObservation, mainInformation } =
		useFRMVisitReportPageContext();
	const { collateral } = officeObservation ?? {};
	const { form, value, fieldName } = useForm<VisitReportCollateralForm>();
	const [modal, setModal] = useState<
		{ type: 'add' } | { type: 'edit'; index: number } | null
	>(null);

	const { data: prospectInformation } = useQuery({
		queryKey: ['getProspectInformation', mainInformation?.prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getProspectInformation(id ?? '');
		},
		enabled: !!mainInformation?.prospectId,
	});

	const isCollateralRequired =
		prospectInformation?.data?.prospectPlafonDetails?.some((detail) =>
			detail.financingTypes?.some((financingType) =>
				financingType.items.some((item) => {
					return [2, 8].includes(item.id);
				}),
			),
		);

	const addFRMOfficeObservationCollateralMutation = useMutation({
		mutationFn: addFRMOfficeObservationCollateral,
	});

	const updateFRMOfficeObservationCollateralMutation = useMutation({
		mutationFn: updateFRMOfficeObservationCollateral,
	});

	const deleteFRMOfficeObservationCollateralMutation = useMutation({
		mutationFn: deleteFRMOfficeObservationCollateral,
	});

	return (
		<Form.Provider>
			<Typography.Title level={3}>
				Detail Unit yang Akan Diagunkan
				{isCollateralRequired && <span style={{ color: 'red' }}>*</span>}
			</Typography.Title>
			{isCollateralRequired && (
				<Alert
					message="Agunan wajib diisi jika jenis pembiayaan termasuk Sale and Leaseback"
					type="warning"
					className="custom-alert"
					showIcon
				/>
			)}
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={
					{
						lists: collateral?.collaterals ?? [],
					} as VisitReportCollateralForm
				}
				onFinish={async ({ input, lists }) => {
					if (modal?.type === 'edit') {
						await updateFRMOfficeObservationCollateralMutation.mutateAsync({
							...input!,
							surveyId: surveyInformation!.id!,
						});
						form.setFieldsValue({
							lists: lists.map((list, index) => {
								return index === modal.index ? input! : list;
							}),
						});
					} else {
						const res =
							await addFRMOfficeObservationCollateralMutation.mutateAsync({
								...input!,
								surveyId: surveyInformation!.id!,
							});
						form.setFieldsValue({
							lists: [...lists, { ...input!, id: res.data }],
						});
					}
					setModal(null);
				}}
				style={{ marginBottom: 30 }}
			>
				<Form.Item
					name={fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={fieldName(['lists'])}
					noStyle
				/>
				<Form.Item shouldUpdate>
					{({ getFieldsValue, setFieldValue }) => {
						const values = getFieldsValue() as VisitReportCollateralForm;
						const dataSource =
							values?.lists?.map((list, key?) => {
								return { ...list, key };
							}) ?? [];

						return (
							<Fragment>
								<Row style={{ display: 'flex', justifyContent: 'end' }}>
									<Button
										style={{
											color: '#1557BC',
											borderRadius: 50,
											fontSize: 12,
											height: 24,
											borderColor: '#1557BC',
											fontWeight: 700,
											marginBottom: 20,
										}}
										loading={
											addFRMOfficeObservationCollateralMutation.isPending ||
											updateFRMOfficeObservationCollateralMutation.isPending
										}
										onClick={() => setModal({ type: 'add' })}
										icon={'+'}
									>
										Add Another
									</Button>
								</Row>
								<Table
									scroll={{
										x: 1200,
									}}
									bordered
									pagination={false}
									dataSource={dataSource}
									columns={[
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											render: (_, record, index) => {
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setFieldValue(fieldName(['input']), record);
																	setModal({
																		type: 'edit',
																		index,
																	});
																}}
															>
																<EditOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={async (e) => {
																	e.preventDefault();
																	const confirmDelete =
																		await modalAPI.modal.confirm({
																			title: 'delete this data?',
																		});

																	if (confirmDelete) {
																		await deleteFRMOfficeObservationCollateralMutation.mutateAsync(
																			record.id!,
																		);
																		form.setFieldsValue({
																			lists: values.lists.filter(
																				(_, listIndex) => listIndex !== index,
																			),
																		});
																	}
																}}
															>
																<DeleteOutlined />
															</a>
														</div>
													</Row>
												);
											},
											width: 1,
										},
										{
											title: 'Jenis Agunan',
											render: (_, record) => record.collateralType ?? '-',
										},
										{
											title: 'Merk/Tipe Serta Kapasitas',
											render: (_, record) => record.brandTypeAndCapacity ?? '-',
										},
										{
											title: 'Tahun Perakitan',
											render: (_, record) => record.yearOfAssembly ?? '-',
										},
										{
											title: 'Kepemilikan',
											render: (_, record) => record.ownership ?? '-',
										},
										{
											title: 'Hour/Meter',
											render: (_, record) => record.hoursMeters ?? '-',
										},
										{
											title: 'Kondisi Fisik',
											render: (_, record) => record.physicalCondition ?? '-',
										},
										{
											title: 'Kondisi Fungsi',
											render: (_, record) => record.functionCondition ?? '-',
										},
										{
											title: 'Keterangan (Jika Ada)',
											render: (_, record) =>
												record.additionalInformation ?? '-',
										},
									]}
								/>
								<FormAlert
									alerts={
										!value?.lists?.length
											? ['Detail Unit yang Akan Diagunkan wajib diisi']
											: []
									}
								/>
								<FleetModal
									open={Boolean(modal)}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									afterClose={() => {
										form.resetFields([fieldName('input')]);
									}}
									onCancel={() => {
										setModal(null);
									}}
									onOk={() => {
										form.submit();
									}}
								>
									<FleetTitle>Add Agunan</FleetTitle>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'id'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'surveyId'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'collateralType'])}
												label="Jenis Agunan"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'brandTypeAndCapacity'])}
												label="Merk/Tipe Serta Kapasitas"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'yearOfAssembly'])}
												label="Tahun Perakitan"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
												getValueProps={(value) => ({
													value: value && dayjs(value),
												})}
												normalize={(value) =>
													value && dayjs(value).format('YYYY')
												}
											>
												<DatePicker.YearPicker />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'ownership'])}
												label="Kepemilikan"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'hoursMeters'])}
												label="Hour/Meter"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'physicalCondition'])}
												label="Kondisi Fisik"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'functionCondition'])}
												label="Kondisi Fungsi"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={24}>
											<Form.Item
												name={fieldName(['input', 'additionalInformation'])}
												label="Keterangan (Jika Ada)"
												labelCol={{
													span: 5,
													style: { marginLeft: -5 },
												}}
											>
												<Input.TextArea rows={3} />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
								</FleetModal>
							</Fragment>
						);
					}}
				</Form.Item>
			</Form>
		</Form.Provider>
	);
};

export default FRMVisitReportOfficeCollateralUnit;
